// src/components/Navbar/Navbar.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/firebase';
import LoginForm from '../Account/LoginForm';
import './Navbar.css';
import logo from '../../assets/scriblish_logo.png';
 
// ─── Design tokens (matches Home.tsx / Footer.tsx) ───────────────────────────
const C = {
    primary:          '#6a37d4',
    primaryLight:     '#ae8dff',
    primaryContainer: '#ede8ff',
    surface:          '#f3f6ff',
    surfaceLow:       '#eaf1ff',
    surfaceContainer: '#dce9fd',
    surfaceHigh:      '#d6e3f9',
    onBackground:     '#252f3c',
    onSurface:        '#252f3c',
    onSurfaceVariant: '#525c6a',
    outlineVariant:   '#a4aebe',
};
 
// ─── Signup dropdown form (logic unchanged, fonts/colors updated) ─────────────
const SignupDropdownForm: React.FC<{ onSuccess: () => void; onSwitchToLogin: () => void }> = ({ onSuccess, onSwitchToLogin }) => {
    const [username, setUsername]               = useState('');
    const [email, setEmail]                     = useState('');
    const [password, setPassword]               = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [accountType, setAccountType]         = useState<'student' | 'parent'>('student');
    const [error, setError]                     = useState('');
    const [loading, setLoading]                 = useState(false);
 
    const inputStyle: React.CSSProperties = {
        width: '100%',
        padding: '9px 12px',
        border: `1.5px solid ${C.outlineVariant}`,
        borderRadius: '8px',
        fontSize: '0.875rem',
        boxSizing: 'border-box',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        outline: 'none',
        transition: 'border-color 0.15s',
        background: '#fff',
    };
 
    const labelStyle: React.CSSProperties = {
        display: 'block',
        marginBottom: '4px',
        fontSize: '0.8rem',
        fontWeight: '700',
        color: C.onSurfaceVariant,
        fontFamily: "'Epilogue', sans-serif",
    };
 
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (password !== confirmPassword) { setError('Passwords do not match'); return; }
        if (password.length < 6)          { setError('Password must be at least 6 characters'); return; }
        if (username.length < 3)          { setError('Username must be at least 3 characters'); return; }
        setLoading(true);
        try {
            console.log('Signup attempt:', { username, email, password, accountType });
            onSuccess();
        } catch (err: any) {
            setError(err.message || 'Failed to create account');
        } finally {
            setLoading(false);
        }
    };
 
    return (
        <div style={{ padding: '24px', width: '320px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: '1.25rem', fontWeight: '900', color: C.onBackground, marginBottom: '6px', textAlign: 'center' }}>
                Join Scriblish
            </h3>
            <p style={{ textAlign: 'center', fontSize: '0.82rem', color: C.onSurfaceVariant, marginBottom: '20px', margin: '0 0 20px' }}>
                Create stories, share ideas. It's free!
            </p>
 
            {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px', marginBottom: '14px', color: '#dc2626', fontSize: '0.82rem', textAlign: 'center' }}>
                    {error}
                </div>
            )}
 
            <form onSubmit={handleSubmit}>
                {/* Account type */}
                <div style={{ marginBottom: '14px' }}>
                    <label style={labelStyle}>I am a:</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {(['student', 'parent'] as const).map(type => (
                            <button
                                key={type}
                                type="button"
                                onClick={() => setAccountType(type)}
                                style={{
                                    flex: 1, padding: '9px',
                                    border: `2px solid ${accountType === type ? C.primary : C.outlineVariant}`,
                                    borderRadius: '8px',
                                    background: accountType === type ? C.primaryContainer : '#fff',
                                    color: accountType === type ? C.primary : C.onSurfaceVariant,
                                    fontSize: '0.82rem', fontWeight: '700',
                                    cursor: 'pointer', transition: 'all 0.15s',
                                    fontFamily: "'Epilogue', sans-serif",
                                    textTransform: 'capitalize',
                                }}
                            >
                                {type}
                            </button>
                        ))}
                    </div>
                </div>
 
                {[
                    { label: 'Username', key: 'username', value: username, setter: setUsername, type: 'text',     placeholder: 'Choose a username', hint: "Don't use your real name" },
                    { label: 'Email',    key: 'email',    value: email,    setter: setEmail,    type: 'email',    placeholder: 'Email address' },
                    { label: 'Password', key: 'pass',     value: password, setter: setPassword, type: 'password', placeholder: 'Create password' },
                    { label: 'Confirm',  key: 'conf',     value: confirmPassword, setter: setConfirmPassword, type: 'password', placeholder: 'Type password again' },
                ].map(field => (
                    <div key={field.key} style={{ marginBottom: '12px' }}>
                        <label style={labelStyle}>{field.label}</label>
                        <input
                            type={field.type}
                            value={field.value}
                            onChange={e => field.setter(e.target.value)}
                            placeholder={field.placeholder}
                            required
                            style={inputStyle}
                            onFocus={e  => { e.target.style.borderColor = C.primary; }}
                            onBlur={e   => { e.target.style.borderColor = C.outlineVariant; }}
                        />
                        {'hint' in field && field.hint && (
                            <div style={{ fontSize: '0.72rem', color: C.onSurfaceVariant, marginTop: '2px' }}>{field.hint}</div>
                        )}
                    </div>
                ))}
 
                <button
                    type="submit"
                    disabled={loading}
                    style={{
                        width: '100%', padding: '11px',
                        background: loading ? C.outlineVariant : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
                        color: '#fff', border: 'none', borderRadius: '9999px',
                        fontSize: '0.9rem', fontWeight: '700',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        marginBottom: '14px', marginTop: '4px',
                        fontFamily: "'Epilogue', sans-serif",
                        transition: 'opacity 0.2s',
                    }}
                >
                    {loading ? 'Creating Account…' : 'Create Account'}
                </button>
            </form>
 
            <div style={{ textAlign: 'center', fontSize: '0.82rem', color: C.onSurfaceVariant }}>
                Already have an account?{' '}
                <button onClick={onSwitchToLogin} style={{ background: 'none', border: 'none', color: C.primary, fontWeight: '700', cursor: 'pointer', fontSize: '0.82rem', fontFamily: "'Epilogue', sans-serif" }}>
                    Sign In
                </button>
            </div>
        </div>
    );
};
 
// ─── Mobile size hook (unchanged) ────────────────────────────────────────────
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return isMobile;
};
 
// ─── Main Navbar ──────────────────────────────────────────────────────────────
const Navbar: React.FC<{
    onNavigateHome: () => void;
    onNavigateStorybook: () => void;
    onNavigateFeaturedStories: () => void;
}> = ({ onNavigateHome, onNavigateStorybook, onNavigateFeaturedStories }) => {
    const { currentUser }    = useAuth();
    const navigate           = useNavigate();
    const location           = useLocation();
    const isMobile           = useIsMobile();
    const [showUserMenu, setShowUserMenu]   = useState(false);
    const [showAuthForm, setShowAuthForm]   = useState<'login' | 'signup' | null>(null);
    const [signingOut, setSigningOut]       = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);
 
    const isLandingPage  = location.pathname === '/' || location.pathname === '/home';
    const showBottomNav =
  currentUser &&
  isMobile &&
  !isLandingPage &&
  !location.pathname.startsWith('/write');
    const isActive = (path: string) => location.pathname === path;
 
    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setShowUserMenu(false);
                setShowAuthForm(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
 
    const handleSignOut = async () => {
        try {
            setSigningOut(true);
            setShowUserMenu(false);
            await signOut(auth);
            navigate('/');
        } catch (error) {
            console.error('Error signing out:', error);
        } finally {
            setSigningOut(false);
        }
    };
 
    const handleAuthSuccess = () => { setShowUserMenu(false); setShowAuthForm(null); };
 
    const getUserInitials = () => {
        if (currentUser?.displayName) return currentUser.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        if (currentUser?.email)       return currentUser.email[0].toUpperCase();
        return '?';
    };
 
 
    // ── Nav link style helper ─────────────────────────────────────────────────
    const navLinkStyle = (active: boolean): React.CSSProperties => ({
        background: active ? C.primaryContainer : 'transparent',
        border: 'none',
        padding: '6px 14px',
        borderRadius: '8px',
        fontSize: '0.9rem',
        fontWeight: '700',
        fontFamily: "'Epilogue', sans-serif",
        cursor: 'pointer',
        color: active ? C.primary : C.onSurfaceVariant,
        transition: 'all 0.15s',
    });
 
    // ── Dropdown item style helper ────────────────────────────────────────────
    const dropdownItemStyle: React.CSSProperties = {
        width: '100%', padding: '11px 16px',
        border: 'none', background: 'transparent',
        textAlign: 'left', cursor: 'pointer',
        fontSize: '0.875rem', fontWeight: '600',
        color: C.onSurface,
        display: 'flex', alignItems: 'center', gap: '10px',
        transition: 'background 0.15s',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
    };
 
    return (
        <>
            {/* ── Top bar ───────────────────────────────────────────────────── */}
            <nav style={{
                position: 'sticky',
                top: 0,
                zIndex: 50,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: isMobile ? '0 16px' : '0 40px',
                height: isMobile ? '58px' : '64px',
                background: C.surface,
                borderBottom: `1px solid ${C.surfaceHigh}`,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}>
                {/* Logo */}
                <div
                    onClick={onNavigateHome}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flexShrink: 0 }}
                >
                    <img
                        src={logo}
                        alt="Scriblish"
                        style={{ height: isMobile ? '30px' : '36px' }}
                    />
                </div>
 
                {/* Desktop nav links */}
                {!(currentUser && isMobile) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <button
                            onClick={onNavigateHome}
                            style={navLinkStyle(isActive('/'))}
                            onMouseEnter={e => { if (!isActive('/')) e.currentTarget.style.background = C.surfaceContainer; }}
                            onMouseLeave={e => { if (!isActive('/')) e.currentTarget.style.background = 'transparent'; }}
                        >
                            Home
                        </button>
                        <button
                            onClick={() => navigate('/write')}
                            style={navLinkStyle(isActive('/write'))}
                            onMouseEnter={e => { if (!isActive('/write')) e.currentTarget.style.background = C.surfaceContainer; }}
                            onMouseLeave={e => { if (!isActive('/write')) e.currentTarget.style.background = 'transparent'; }}
                        >
                            Write
                        </button>
                        <button
                            onClick={onNavigateFeaturedStories}
                            style={navLinkStyle(isActive('/featured-stories'))}
                            onMouseEnter={e => { if (!isActive('/featured-stories')) e.currentTarget.style.background = C.surfaceContainer; }}
                            onMouseLeave={e => { if (!isActive('/featured-stories')) e.currentTarget.style.background = 'transparent'; }}
                        >
                            Stories
                        </button>
                        <button
                            onClick={() => navigate('/prompts')}
                            style={navLinkStyle(isActive('/prompts'))}
                            onMouseEnter={e => { if (!isActive('/prompts')) e.currentTarget.style.background = C.surfaceContainer; }}
                            onMouseLeave={e => { if (!isActive('/prompts')) e.currentTarget.style.background = 'transparent'; }}
                        >
                            Prompts
                        </button>
                        <button
                            onClick={() => navigate('/dashboard')}
                            style={navLinkStyle(isActive('/dashboard'))}
                            onMouseEnter={e => { if (!isActive('/dashboard')) e.currentTarget.style.background = C.surfaceContainer; }}
                            onMouseLeave={e => { if (!isActive('/dashboard')) e.currentTarget.style.background = 'transparent'; }}
                        >
                            Dashboard
                        </button>
                    </div>
                )}
 
                {/* Right side */}
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flexShrink: 0 }}>
                    {/* Desktop search */}
                    {!isMobile && (
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                placeholder="Search stories…"
                                style={{
                                    padding: '7px 14px 7px 36px',
                                    border: `1.5px solid ${C.outlineVariant}`,
                                    borderRadius: '9999px',
                                    fontSize: '0.85rem',
                                    background: C.surfaceLow,
                                    color: C.onSurface,
                                    outline: 'none',
                                    width: '180px',
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    transition: 'border-color 0.15s, width 0.2s',
                                }}
                                onFocus={e  => { e.target.style.borderColor = C.primary; e.target.style.width = '220px'; }}
                                onBlur={e   => { e.target.style.borderColor = C.outlineVariant; e.target.style.width = '180px'; }}
                            />
                            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem', color: C.onSurfaceVariant, pointerEvents: 'none' }}>
                                🔍
                            </span>
                        </div>
                    )}
 
                    {/* User menu */}
                    <div className="user-menu-container" ref={userMenuRef} style={{ position: 'relative' }}>
                        {currentUser ? (
                            /* ── Avatar button (logged in) ────────────────── */
                            <>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    style={{
                                        width: isMobile ? '34px' : '38px',
                                        height: isMobile ? '34px' : '38px',
                                        borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
                                        color: '#fff',
                                        border: 'none',
                                        fontSize: isMobile ? '12px' : '13px',
                                        fontWeight: '800',
                                        cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: showUserMenu ? `0 0 0 3px ${C.primaryContainer}` : 'none',
                                        transition: 'box-shadow 0.15s',
                                        fontFamily: "'Epilogue', sans-serif",
                                        flexShrink: 0,
                                    }}
                                >
                                    {getUserInitials()}
                                </button>
 
                                {showUserMenu && (
                                    <div style={{
                                        position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                                        background: '#fff', borderRadius: '16px',
                                        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                                        border: `1px solid ${C.surfaceHigh}`,
                                        minWidth: '220px', zIndex: 1000, overflow: 'hidden',
                                        animation: 'navDropIn 0.18s ease-out',
                                    }}>
                                        {/* User header */}
                                        <div style={{
                                            padding: '16px',
                                            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
                                            color: '#fff',
                                        }}>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '800', fontFamily: "'Epilogue', sans-serif", marginBottom: '2px' }}>
                                                {currentUser.displayName || 'User'}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', opacity: 0.85 }}>{currentUser.email}</div>
                                        </div>
 
                                        <div style={{ padding: '6px 0' }}>
                                            {[
                                                { icon: '👤', label: 'My Profile',    action: () => { setShowUserMenu(false); navigate('/profile'); } },
                                                { icon: '❓', label: 'Help & Support', action: () => { setShowUserMenu(false); navigate('/help'); } },
                                            ].map(item => (
                                                <button
                                                    key={item.label}
                                                    onClick={item.action}
                                                    style={dropdownItemStyle}
                                                    onMouseEnter={e => { e.currentTarget.style.background = C.surfaceLow; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                                >
                                                    <span>{item.icon}</span>
                                                    <span>{item.label}</span>
                                                </button>
                                            ))}
 
                                            <div style={{ height: '1px', background: C.surfaceHigh, margin: '4px 12px' }} />
 
                                            <button
                                                onClick={handleSignOut}
                                                disabled={signingOut}
                                                style={{ ...dropdownItemStyle, color: signingOut ? C.onSurfaceVariant : '#dc2626', cursor: signingOut ? 'not-allowed' : 'pointer' }}
                                                onMouseEnter={e => { if (!signingOut) e.currentTarget.style.background = '#fef2f2'; }}
                                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                            >
                                                <span>{signingOut ? '⏳' : '🚪'}</span>
                                                <span>{signingOut ? 'Signing out…' : 'Sign Out'}</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            /* ── Auth button (logged out) ─────────────────── */
                            <>
                                {!isMobile && (
                                    <button
                                        onClick={() => navigate('/login')}
                                        style={{
                                            background: 'none', border: 'none',
                                            color: C.onSurfaceVariant,
                                            fontSize: '0.9rem', fontWeight: '700',
                                            cursor: 'pointer', padding: '6px 14px',
                                            fontFamily: "'Epilogue', sans-serif",
                                            transition: 'color 0.15s',
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.color = C.primary; }}
                                        onMouseLeave={e => { e.currentTarget.style.color = C.onSurfaceVariant; }}
                                    >
                                        Log In
                                    </button>
                                )}
 
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    style={{
                                        background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%)`,
                                        color: '#fff', border: 'none',
                                        borderRadius: '9999px',
                                        padding: isMobile ? '8px 18px' : '9px 22px',
                                        fontSize: '0.875rem', fontWeight: '700',
                                        cursor: 'pointer',
                                        fontFamily: "'Epilogue', sans-serif",
                                        transition: 'opacity 0.15s, transform 0.15s',
                                        boxShadow: `0 4px 14px ${C.primary}40`,
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.opacity = '1';   e.currentTarget.style.transform = 'translateY(0)'; }}
                                >
                                    Sign Up Free
                                </button>
 
                                {/* Auth dropdown */}
                                {showUserMenu && (
                                    <div style={{
                                        position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                                        background: '#fff', borderRadius: '16px',
                                        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                                        border: `1px solid ${C.surfaceHigh}`,
                                        zIndex: 10000, overflow: 'visible',
                                        animation: 'navDropIn 0.18s ease-out',
                                    }}>
                                        {!showAuthForm ? (
                                            <div style={{ padding: '8px 0', minWidth: '160px' }}>
                                                {[
                                                    { label: 'Sign In',        action: () => setShowAuthForm('login') },
                                                    { label: 'Create Account', action: () => navigate('/signup') },
                                                ].map((item, i) => (
                                                    <React.Fragment key={item.label}>
                                                        {i === 1 && <div style={{ height: '1px', background: C.surfaceHigh, margin: '4px 12px' }} />}
                                                        <button
                                                            onClick={item.action}
                                                            style={dropdownItemStyle}
                                                            onMouseEnter={e => { e.currentTarget.style.background = C.surfaceLow; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                                        >
                                                            {item.label}
                                                        </button>
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        ) : (
                                            <div>
                                                {showAuthForm === 'login' ? (
                                                    <LoginForm
                                                        variant="dropdown"
                                                        onSuccess={handleAuthSuccess}
                                                        onSwitchToSignup={() => { setShowUserMenu(false); setShowAuthForm(null); navigate('/signup'); }}
                                                    />
                                                ) : (
                                                    <SignupDropdownForm
                                                        onSuccess={handleAuthSuccess}
                                                        onSwitchToLogin={() => setShowAuthForm('login')}
                                                    />
                                                )}
                                                <div style={{ background: C.surfaceLow, padding: '10px', textAlign: 'center', borderTop: `1px solid ${C.surfaceHigh}` }}>
                                                    <button
                                                        onClick={() => setShowAuthForm(null)}
                                                        style={{ background: 'none', border: 'none', color: C.onSurfaceVariant, fontSize: '0.8rem', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                                                    >
                                                        ← Back
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
 
                <style>{`
                    @keyframes navDropIn {
                        from { opacity: 0; transform: translateY(-8px) scale(0.97); }
                        to   { opacity: 1; transform: translateY(0)     scale(1);    }
                    }
                `}</style>
            </nav>
 
            {/* ── Bottom mobile nav (logged-in only, not on landing) ─────────── */}
            {showBottomNav && (
                <div style={{
                    position: 'fixed', bottom: 0, left: 0, right: 0,
                    height: '60px',
                    background: '#fff',
                    borderTop: `1px solid ${C.surfaceHigh}`,
                    display: 'flex', justifyContent: 'space-around', alignItems: 'center',
                    zIndex: 1000,
                    boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}>
                    {[
                        { icon: '📖', label: 'My Stories', path: '/my-stories' },
                        { icon: '✏️', label: 'Write',      path: '/write'      },
                        { icon: '📚', label: 'Stories',    path: '/featured-stories' },
                        { icon: '📊', label: 'Dashboard',  path: '/dashboard'  },
                    ].map(item => {
                        const active = isActive(item.path);
                        return (
                            <button
                                key={item.path}
                                onClick={() => navigate(item.path)}
                                style={{
                                    flex: 1, display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center',
                                    background: 'none', border: 'none',
                                    padding: '8px', cursor: 'pointer',
                                    color: active ? C.primary : C.onSurfaceVariant,
                                    transition: 'color 0.15s',
                                }}
                            >
                                <span style={{ fontSize: '22px' }}>{item.icon}</span>
                                <span style={{ fontSize: '10px', marginTop: '2px', fontWeight: active ? '700' : '500' }}>
                                    {item.label}
                                </span>
                                {active && (
                                    <div style={{ position: 'absolute', bottom: 0, width: '24px', height: '2px', background: C.primary, borderRadius: '1px' }} />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
 
        </>
    );
};
 
export default Navbar;
